import { useEffect, useState } from "react";
import { Download, Smartphone, X } from "lucide-react";

const VISIT_KEY = "dp-pwa-visit-count";
const DISMISSED_KEY = "dp-pwa-install-dismissed";
const MIN_VISITS = 3;

type BeforeInstallEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  // iOS Safari
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((navigator as any).standalone === true) return true;
  // Andere
  return window.matchMedia("(display-mode: standalone)").matches;
}

function isiOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function getVisits(): number {
  try {
    return parseInt(localStorage.getItem(VISIT_KEY) || "0", 10) || 0;
  } catch {
    return 0;
  }
}
function bumpVisits(): number {
  const v = getVisits() + 1;
  try {
    localStorage.setItem(VISIT_KEY, String(v));
  } catch {
    /* noop */
  }
  return v;
}
function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return true;
  }
}
function dismiss(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, "1");
  } catch {
    /* noop */
  }
}

export function PwaInstallPrompt() {
  const [show, setShow] = useState(false);
  const [installEvent, setInstallEvent] = useState<BeforeInstallEvent | null>(null);
  const ios = isiOS();

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;
    // Wenn aktive Tour läuft, blockieren wir den Install-Prompt — sonst overlap
    if (document.body.hasAttribute("data-tour-active")) return;

    const visits = bumpVisits();
    if (visits < MIN_VISITS) return;

    // Chrome/Edge/Android: warte auf beforeinstallprompt
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallEvent);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS: Event existiert nicht, manuelle Anleitung nach kurzer Wartezeit
    let iosTimer: number | undefined;
    if (ios) {
      iosTimer = window.setTimeout(() => setShow(true), 1500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, [ios]);

  // Wenn App installiert wird → ausblenden
  useEffect(() => {
    const onInstalled = () => {
      setShow(false);
      dismiss();
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  if (!show) return null;

  const onClose = () => {
    dismiss();
    setShow(false);
  };

  const install = async () => {
    if (!installEvent) return;
    try {
      await installEvent.prompt();
      const choice = await installEvent.userChoice;
      if (choice.outcome === "dismissed") {
        dismiss();
      }
    } catch {
      /* noop */
    } finally {
      setShow(false);
    }
  };

  return (
    <div className="fab-bottom-center z-30 w-[min(440px,calc(100vw-2rem))] print:hidden animate-fade-in"
         style={{ marginBottom: "60px" }}
    >
      <div className="rounded-2xl bg-white shadow-2xl border border-line p-4 flex gap-3">
        <div className="grid size-10 place-items-center rounded-xl brand-gradient text-white flex-shrink-0">
          <Smartphone className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">DP Connect als App installieren</div>
          {ios ? (
            <p className="text-xs text-ink-muted mt-0.5">
              Tippe in Safari unten auf{" "}
              <span className="font-medium">Teilen-Symbol</span> → „Zum Home-Bildschirm".
            </p>
          ) : (
            <p className="text-xs text-ink-muted mt-0.5">
              Du nutzt das Portal oft — installier es als App fuer schnelleren Zugriff.
            </p>
          )}
          {!ios && installEvent && (
            <button
              onClick={install}
              className="mt-2 inline-flex items-center gap-1 rounded-lg brand-gradient text-white px-3 py-1.5 text-xs font-semibold"
            >
              <Download className="size-3" /> Installieren
            </button>
          )}
        </div>
        <button
          onClick={onClose}
          className="size-7 grid place-items-center rounded-full text-ink-muted hover:bg-cream-soft hover:text-ink flex-shrink-0"
          aria-label="Nicht jetzt"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
